import { HeaderNames } from '@/@vxapp/@Enums/PHP/HTTP/HeaderNames'

const MAXIMUM_RESPONSE_BYTES = 65_536

/* eslint-disable no-unused-vars -- Enum members define the closed HTTP method contract. */
enum NativePushApiMethod {
  Delete = 'DELETE',
  Post = 'POST',
}
/* eslint-enable no-unused-vars */

export interface NativePushApiResponse<ResponseData> {
  data: ResponseData
}

export interface NativePushApiRequestOptions {
  headers: Record<string, string>
  timeoutMs: number
}

export class NativePushApiRequestError extends Error {
  readonly response: {
    data: unknown
    status: number | null
  }

  constructor(status: number | null, data: unknown = {}) {
    super('Native push API request failed')
    this.name = 'NativePushApiRequestError'
    this.response = { data, status }
  }
}

const parseApiUrl = (candidate: string): URL => {
  try {
    const parsedUrl = new URL(candidate)

    if (
      parsedUrl.protocol !== 'https:' ||
      parsedUrl.username ||
      parsedUrl.password ||
      parsedUrl.search ||
      parsedUrl.hash
    ) {
      throw new NativePushApiRequestError(null)
    }

    return parsedUrl
  } catch (error: unknown) {
    if (error instanceof NativePushApiRequestError) {
      throw error
    }

    throw new NativePushApiRequestError(null)
  }
}

const readHeader = (headers: Record<string, string>, expectedName: HeaderNames): string | null => {
  const entry = Object.entries(headers).find(
    ([headerName]) => headerName.toLowerCase() === expectedName.toLowerCase()
  )

  return typeof entry?.[1] === 'string' && entry[1].length > 0 ? entry[1] : null
}

const getClosedHeaders = (headers: Record<string, string>): Record<string, string> => {
  const authorization = readHeader(headers, HeaderNames.AUTH_JWT)
  const apiGuid = readHeader(headers, HeaderNames.API_GUID)

  if (!authorization || !apiGuid) {
    throw new NativePushApiRequestError(null)
  }

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    [HeaderNames.AUTH_JWT]: authorization,
    [HeaderNames.API_GUID]: apiGuid,
  }
}

const readResponseData = async (response: Response): Promise<unknown> => {
  const responseText = await response.text()

  if (responseText.length > MAXIMUM_RESPONSE_BYTES) {
    throw new NativePushApiRequestError(response.status)
  }

  if (responseText.length === 0) {
    return {}
  }

  try {
    const data: unknown = JSON.parse(responseText)

    return typeof data === 'object' && data !== null ? data : {}
  } catch {
    return {}
  }
}

const requestNativePushApi = async <ResponseData>(
  method: NativePushApiMethod,
  url: string,
  body: Record<string, unknown>,
  options: NativePushApiRequestOptions
): Promise<NativePushApiResponse<ResponseData>> => {
  const parsedUrl = parseApiUrl(url)
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), options.timeoutMs)

  try {
    const response = await fetch(parsedUrl.href, {
      body: JSON.stringify(body),
      credentials: 'include',
      headers: getClosedHeaders(options.headers),
      method,
      redirect: 'error',
      signal: controller.signal,
    })

    const data = await readResponseData(response)

    if (!response.ok) {
      throw new NativePushApiRequestError(response.status, data)
    }

    return { data: data as ResponseData }
  } catch (error: unknown) {
    if (error instanceof NativePushApiRequestError) {
      throw error
    }

    // Fetch failures can contain request URLs and browser-specific network details.
    throw new NativePushApiRequestError(null)
  } finally {
    window.clearTimeout(timeout)
  }
}

export const postNativePushApi = <ResponseData>(
  url: string,
  body: Record<string, unknown>,
  options: NativePushApiRequestOptions
): Promise<NativePushApiResponse<ResponseData>> =>
  requestNativePushApi(NativePushApiMethod.Post, url, body, options)

export const deleteNativePushApi = <ResponseData>(
  url: string,
  body: Record<string, unknown>,
  options: NativePushApiRequestOptions
): Promise<NativePushApiResponse<ResponseData>> =>
  requestNativePushApi(NativePushApiMethod.Delete, url, body, options)
